/* eslint-disable max-lines */

import _ from 'lodash';
import moment from 'moment-timezone';
import { toDomesticTel } from '@this/src/util/string';
import { isAiTravel } from '@this/src/util/utils';
import type OrderItem from '@this/domain/order_item';
import type SuppliedItem from '@this/domain/supplied_item/supplied_item';
import type { IndividualDomesticAirSuppliedItem } from '@this/domain/individual_domestic_air_supplied_item';
import type React from 'react';
import TransportElementAttachFile from '@this/domain/transport_element_attach_file';
import Aviation from '@this/domain/aviation';
import type { AviationArgs } from '@this/domain/aviation';
import type { TransportElementTerminalJson } from '@this/domain/transport_element_terminal/transport_element_terminal';
import type ElementBaseInterface from '@this/domain/element_base_interface';
import Terminal from '../transport_element_terminal';
import TimeZoneLib from '../../../../lib/time_zone.json';
import type { TransportElementAttachFileArgs } from '../transport_element_attach_file';

type TransportType =
  | 'domestic_air'
  | 'foreign_air'
  | 'shinkansen'
  | 'express'
  | 'railway_ticket'
  | 'rental_car'
  | 'bus'
  | 'ana_tabisaku'
  | 'taxi'
  | 'foreign_railway'
  | 'other';

type CarType = 'compact' | 'sedan' | 'other';

type CarProvider =
  | 'unselected'
  | 'times'
  | 'nihon_kuyu'
  | 'racco'
  | 'eki_rentacar'
  | 'smas_rentacar'
  | 'nippon_rentacar'
  | 'orix_rentacar'
  | 'toyota_rentacar';

type AirClass = 'F' | 'C' | 'W' | 'M';

type TaxiService = 'smartRyde';

type Cabin = 'F' | 'C' | 'W' | 'M' | '';

// 0:AIトラベル, 1:アリーズ, 2:エボラブルアジア, 3:IWAツアー, 4:旅工房, 5:フロンティア, 6:スカイツアーズ, 7:ANA（代理店）
type TicketingType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

// 0:変更可, 1:変更不可, 2:WBFパッケージ, 3:楽天パッケージ
type AirTicketType = 0 | 1 | 2 | 3;
export const AIR_TICKET_TYPE_RAKUTEN_PACKAGE: AirTicketType = 3;

type UploadFileData = {
  upload_file_name: string;
  content_type: string;
  upload_file_base64: string;
};

// TODO: 必要に応じてスネークケースに変更
export type TransportElementJson = {
  id?: number;
  transportType?: TransportType;
  name?: string;
  from?: unknown;
  to?: unknown;
  railwaySeatType?: number;
  airTicketType?: AirTicketType;
  airSeatType?: string;
  peopleNum?: number;
  boardingStation?: string;
  arrivalStation?: string;
  postingNumber?: string;
  autoReservationStatus?: number;
  soldOut?: boolean;
  flightTax?: string;
  ticketingType?: TicketingType;
  evoApplicationNumber?: string;
  evoTicketType?: string;
  evoReservationNumber?: string;
  evoConfirmationNumber?: string;
  evoSkyPdf?: string;
  carType?: CarType;
  carTypeOther?: string;
  remarks?: string;
  firstNameKana?: string;
  lastNameKana?: string;
  carNumber?: number;
  driverTel?: string;
  carProvider?: CarProvider;
  exicReceiptNumber?: string;
  airReservationNumber?: string;
  airConfirmationNumber?: string;
  airSeatNumber?: string;
  sakuraTicketType?: string;
  railwayTicketType?: number;
  ticketingExpiredAt?: string;
  transportItineraryId?: number;
  wbfBinCode?: string;
  ticketingAt?: string;
  airClass?: AirClass;
  airlineCode?: string;
  wbfClassCode?: string;
  wbfTimetypeCode?: string;
  wbfSupplierCode?: string;
  railwayDistance?: number;
  vincentyDistance?: number;
  alliesApplicationNumber?: string;
  frontierApplicationNumber?: string;
  skytoursApplicationNumber?: string;
  distanceData?: string;
  routeTimeData?: string;
  reservationNumber?: string;
  ticketNumber?: string;
  taxiService?: TaxiService;
  cabin?: Cabin;

  airlineName?: string;
  aviations?: unknown[];
  transportElementReservations?: { [p: string]: unknown };
  ticketOprReservations?: { [p: string]: unknown };

  carTypeOptions?: Array<[string, string]>;
  carProviderOptions?: Array<[string, string]>;
  transportElementAttachFile?: unknown;
  serviceId?: number;
};

class TransportElement implements ElementBaseInterface {
  id: number | undefined;

  transportType: TransportType;

  type: string;

  name: string;

  from: Terminal;

  to: Terminal;

  railwaySeatType: number | undefined;

  airTicketType: AirTicketType;

  airSeatType: string | undefined;

  peopleNum: number | undefined;

  boardingStation: string;

  arrivalStation: string;

  postingNumber: string;

  autoReservationStatus: number | undefined;

  soldOut: boolean | undefined;

  flightTax: string | undefined;

  ticketingType: TicketingType;

  evoApplicationNumber: string | undefined;

  evoTicketType: string | undefined;

  evoReservationNumber: string | undefined;

  evoConfirmationNumber: string | undefined;

  evoSkyPdf: string | undefined;

  carType: CarType | undefined;

  carTypeOther: string | undefined;

  remarks: string | undefined;

  firstNameKana: string | undefined;

  lastNameKana: string | undefined;

  carNumber: number | undefined;

  driverTel: string | undefined;

  carProvider: CarProvider | undefined;

  exicReceiptNumber: string;

  airReservationNumber: string;

  airConfirmationNumber: string;

  airSeatNumber: string;

  sakuraTicketType: string | undefined;

  railwayTicketType: number | undefined;

  ticketingExpiredAt: string;

  transportItineraryId: number | undefined;

  wbfBinCode: string | undefined;

  ticketingAt: moment.Moment | undefined;

  airClass: AirClass;

  airlineCode: string;

  wbfClassCode: string | undefined;

  wbfTimetypeCode: string | undefined;

  wbfSupplierCode: string | undefined;

  railwayDistance: number | undefined;

  vincentyDistance: number | undefined;

  alliesApplicationNumber: string | undefined;

  frontierApplicationNumber: string | undefined;

  skytoursApplicationNumber: string | undefined;

  distanceData: string | undefined;

  routeTimeData: string | undefined;

  reservationNumber: string;

  ticketNumber: string;

  taxiService: TaxiService | undefined;

  cabin: Cabin;

  airlineName: string;

  aviations: Aviation[];

  // transportElementReservations: Array<{ [p: string]: unknown }>;
  transportElementReservations: unknown[];

  ticketOprReservations: unknown[];

  carTypeOptions: Array<[string, string]>;

  carProviderOptions: Array<[string, string]>;

  selectTimeZone: Array<{ label: string; value: string }>;

  showFromTimeZoneList: boolean;

  showToTimeZoneList: boolean;

  filteredFromTimeZone: Array<{ label: string; value: string }> | undefined;

  filteredToTimeZone: Array<{ label: string; value: string }> | undefined;

  transportElementAttachFile: TransportElementAttachFile | undefined;

  uploadFileData: UploadFileData | null;

  serviceId: number;

  constructor(args: TransportElementJson = {}) {
    Object.assign(this, args);
    this.type = 'transport';
    this.from = new Terminal(args.from as TransportElementTerminalJson);
    this.to = new Terminal(args.to as TransportElementTerminalJson);
    this.transportType = args.transportType || 'domestic_air';
    this.airlineCode = args.airlineCode || '';
    this.airlineName = args.airlineName || '';
    this.airTicketType = args.airTicketType || 0;
    this.cabin = args.cabin || '';
    this.ticketingType = args.ticketingType || 0;
    this.boardingStation = args.boardingStation || '';
    this.arrivalStation = args.arrivalStation || '';
    this.postingNumber = args.postingNumber || '';
    this.exicReceiptNumber = args.exicReceiptNumber || '';
    this.aviations = _.map(args.aviations, a => new Aviation(a as AviationArgs));
    this.selectTimeZone = TimeZoneLib;
    this.showFromTimeZoneList = false;
    this.showToTimeZoneList = false;
    this.filteredFromTimeZone = [];
    this.filteredToTimeZone = [];
    this.airReservationNumber = args.airReservationNumber || '';
    this.airConfirmationNumber = args.airConfirmationNumber || '';
    this.airSeatNumber = args.airSeatNumber || '';
    this.airClass = args.airClass || 'M';
    this.ticketingExpiredAt = args.ticketingExpiredAt || '';
    this.ticketingAt = args.ticketingAt ? moment(args.ticketingAt) : undefined;
    this.carTypeOptions = args.carTypeOptions || [];
    this.carProviderOptions = args.carProviderOptions || [];
    this.serviceId = args.serviceId || 1;
    this.name = args.name || '';

    // convert the reservation into array from object
    if (args.transportElementReservations) {
      this.transportElementReservations = Object.values(args.transportElementReservations);
    } else {
      this.transportElementReservations = [];
    }

    // convert the reservation into array from object
    if (args.ticketOprReservations) {
      this.ticketOprReservations = Object.values(args.ticketOprReservations);
    } else {
      this.ticketOprReservations = [];
    }

    this.reservationNumber = args.reservationNumber || '';
    this.ticketNumber = args.ticketNumber || '';
    this.carProviderOptions = args.carProviderOptions || [];
    this.carTypeOptions = args.carTypeOptions || [];
    this.transportElementAttachFile = args.transportElementAttachFile
      ? new TransportElementAttachFile(args.transportElementAttachFile as TransportElementAttachFileArgs)
      : undefined;
    this.uploadFileData = null;
  }

  handleTransportTypeChange(transportType: string) {
    this.transportType = transportType as TransportType;
    return app.render();
  }

  handleAirTicketTypeChange(value: string) {
    this.airTicketType = parseInt(value, 10) as AirTicketType;
    return app.render();
  }

  handleCabinChange(value: string) {
    this.cabin = value as Cabin;
    return app.render();
  }

  handleTicketingTypeChange(value: string) {
    this.ticketingType = parseInt(value, 10) as TicketingType;
    return app.render();
  }

  handleTicketingTypeChangeWithSuppliedId(
    value: string,
    orderItem: OrderItem,
    suppliedItems: SuppliedItem[],
    individualDomesticAirSuppliedItems: IndividualDomesticAirSuppliedItem[]
  ) {
    this.ticketingType = parseInt(value, 10) as TicketingType;
    const { name } = this;
    const { ticketingType } = this;
    orderItem.price.originalPrices = _.map(orderItem.price.originalPrices, originalPrice => {
      originalPrice.individualSuppliedItemId(
        name,
        ticketingType,
        suppliedItems,
        individualDomesticAirSuppliedItems
      );
      return originalPrice;
    });
    return app.render();
  }

  handleRailwaySeatTypeChange(value: string) {
    this.railwaySeatType = parseInt(value, 10);
    return app.render();
  }

  handleRailwayTicketTypeChange(value: string) {
    this.railwayTicketType = parseInt(value, 10);
    return app.render();
  }

  handleCarProviderChange(value: string) {
    this.carProvider = value as CarProvider;
    return app.render();
  }

  handleTaxiServiceChange(value: string) {
    this.taxiService = value as TaxiService;
    return app.render();
  }

  handleNameChange(value: string) {
    this.name = value;
    return app.render();
  }

  handleBoardingStationChange(value: string, changeFromName = false) {
    this.boardingStation = value;
    if (changeFromName) {
      this.from.name = value;
    }
    return app.render();
  }

  handleArrivalStationChange(value: string, changeToName = false) {
    this.arrivalStation = value;
    if (changeToName) {
      this.to.name = value;
    }
    return app.render();
  }

  handlePeopleNumChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    this.peopleNum = parseInt(e.target.value, 10);
    return app.render();
  }

  handlePostingNumberChange(value: string) {
    this.postingNumber = value;
    return app.render();
  }

  handleExicReceiptNumberChange(value: string) {
    this.exicReceiptNumber = value;
    return app.render();
  }

  toggleFromTimeZoneList() {
    this.showFromTimeZoneList = !this.showFromTimeZoneList;
    if (this.showFromTimeZoneList) {
      this.initialFromTimeZoneList();
    } else {
      this.filteredFromTimeZone = [];
    }
    return app.render();
  }

  toggleToTimeZoneList() {
    this.showToTimeZoneList = !this.showToTimeZoneList;
    if (this.showToTimeZoneList) {
      this.initialToTimeZoneList();
    } else {
      this.filteredToTimeZone = [];
    }
    return app.render();
  }

  handleFromTimeZoneChange(timezone: string) {
    this.from.changeTimeZone(timezone);
    this.toggleFromTimeZoneList();
    return app.render();
  }

  handleToTimeZoneChange(timezone: string) {
    this.to.changeTimeZone(timezone);
    this.toggleToTimeZoneList();
    return app.render();
  }

  handleAirlineCodeChange(value: string) {
    this.airlineCode = value;
    return app.render();
  }

  handleAirReservationNumberChange(value: string) {
    this.airReservationNumber = value;
    return app.render();
  }

  handleAirConfirmationNumberChange(value: string) {
    this.airConfirmationNumber = value;
    return app.render();
  }

  handleAirClassChange(value: string) {
    this.airClass = value as AirClass;
    return app.render();
  }

  handleCarTypeChange(value: string) {
    this.carType = value as CarType;
    return app.render();
  }

  handleCarTypeOtherChange(value: string) {
    this.carTypeOther = value;
    return app.render();
  }

  handleLastNameKanaChange(value: string) {
    this.lastNameKana = value;
    return app.render();
  }

  handleFirstNameKanaChange(value: string) {
    this.firstNameKana = value;
    return app.render();
  }

  handleDriverTelChange(value: string) {
    this.driverTel = value;
    return app.render();
  }

  handleRemarksChange(value: string) {
    this.remarks = value;
    return app.render();
  }

  handleAlliesApplicationNumber(value: string) {
    this.alliesApplicationNumber = value;
    return app.render();
  }

  handleFrontierApplicationNumber(value: string) {
    this.frontierApplicationNumber = value;
    return app.render();
  }

  handleSkytoursApplicationNumber(value: string) {
    this.skytoursApplicationNumber = value;
    return app.render();
  }

  handleReservationNumberChange(value: string) {
    this.reservationNumber = value;
    return app.render();
  }

  handleTicketNumberChange(value: string) {
    this.ticketNumber = value;
    return app.render();
  }

  initialFromTimeZoneList() {
    const fromTimezone = this.from.timeZone.split('/')[0];
    this.filteredFromTimeZone = TimeZoneLib.filter(
      t => t.value.indexOf(fromTimezone) !== -1 || t.value.toLowerCase().indexOf(fromTimezone) !== -1
    );
    return app.render();
  }

  initialToTimeZoneList() {
    const toTimezone = this.to.timeZone.split('/')[0];
    this.filteredToTimeZone = TimeZoneLib.filter(
      t => t.value.indexOf(toTimezone) !== -1 || t.value.toLowerCase().indexOf(toTimezone) !== -1
    );
    return app.render();
  }

  handleSearchTimeZone(e: React.ChangeEvent<HTMLInputElement>) {
    let filtered;
    const keyword = e.target.value;
    if (_.isEmpty(keyword)) {
      if (e.target.name === 'fromTimeZone') {
        const fromTimezone = this.from.timeZone.split('/')[0];
        filtered = TimeZoneLib.filter(
          t => t.value.indexOf(fromTimezone) !== -1 || t.value.toLowerCase().indexOf(fromTimezone) !== -1
        );
      } else if (e.target.name === 'toTimeZone') {
        const toTimezone = this.to.timeZone.split('/')[0];
        filtered = TimeZoneLib.filter(
          t => t.value.indexOf(toTimezone) !== -1 || t.value.toLowerCase().indexOf(toTimezone) !== -1
        );
      }
    } else {
      filtered = TimeZoneLib.filter(
        t => t.value.indexOf(keyword) !== -1 || t.value.toLowerCase().indexOf(keyword) !== -1
      );
    }

    if (e.target.name === 'fromTimeZone') {
      this.filteredFromTimeZone = filtered;
    } else {
      this.filteredToTimeZone = filtered;
    }
    return app.render();
  }

  handleTicketTypeChange(value: string) {
    if (this.isEvoNumbers()) {
      this.evoTicketType = value;
    } else {
      this.sakuraTicketType = value;
    }
    return app.render();
  }

  handleSeatTypeChange(value: string) {
    this.airSeatType = value;

    return app.render();
  }

  handleTicketingAtChange(date: moment.Moment) {
    this.ticketingAt = date;
    return app.render();
  }

  handleUploadFileData(uploadFileData: UploadFileData) {
    this.uploadFileData = uploadFileData;
    return app.render();
  }

  calcFlightTime() {
    const fromTime = this.from.time.clone();
    const toTime = this.to.time.clone();
    const h = String(Math.floor(toTime.diff(fromTime) / 3600000) + 100).substring(1);
    const m = String(Math.floor((toTime.diff(fromTime) - parseInt(h, 10) * 3600000) / 60000) + 100).substring(1);
    return `搭乗時間 : ${h}時間${m}分`;
  }

  startDate() {
    return this.from.time;
  }

  endDate() {
    return this.to.time;
  }

  // FIXME: typo
  ticketingExpiredAtStr() {
    return moment(this.ticketingExpiredAt).format('YYYY月MM月DD日HH時mm分');
  }

  isTicketingExpired() {
    return moment(this.ticketingExpiredAt) < moment();
  }

  title() {
    switch (this.transportType) {
      case 'domestic_air':
        return '国内航空券';
      case 'foreign_air':
        return '海外航空券';
      case 'shinkansen':
        return '新幹線';
      case 'express':
        return '特急';
      case 'railway_ticket':
        return '在来線';
      case 'foreign_railway':
        return '海外鉄道';
      case 'rental_car':
        return 'レンタカー';
      case 'bus':
        return 'バス';
      case 'ana_tabisaku':
        return 'ANA旅作';
      case 'taxi':
        return 'タクシー';
      case 'other':
        return 'その他';
      default:
        return '';
    }
  }

  railwaySeatTypeStr() {
    if (this.isRailway()) {
      switch (this.railwaySeatType) {
        case 0:
          return '自由席';
        case 1:
          return '指定席';
        case 2:
          return 'グリーン車';
        case 3:
          return '回数券';
        case 4:
          return 'EX-IC';
        case 5:
          return 'グランクラス';
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  travelerSeatTypeStr(orderItem: OrderItem | undefined = undefined) {
    if (!this.isRailway()) {
      return '';
    }

    const seatType = this.railwaySeatTypeStr();
    if (!_.isEmpty(seatType)) {
      return seatType;
    }

    if (!orderItem) {
      return '';
    }

    const seatTypes = Array.from(new Set(orderItem.travelerShinkansenSeatTypes()));
    return seatTypes.join('、');
  }

  railwayTicketTypeStr() {
    if (this.isRailway()) {
      switch (this.railwayTicketType) {
        case 0:
          return '通常きっぷ';
        case 1:
          return 'JR九州きっぷ';
        case 2:
          return '回数券';
        case 3:
          return 'EX-IC';
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  travelerTicketTypeStr(orderItem: OrderItem | undefined) {
    if (!this.isRailway()) {
      return '';
    }

    const ticketType = this.railwayTicketTypeStr();
    if (!_.isEmpty(ticketType)) {
      return ticketType;
    }

    if (!orderItem) {
      return '';
    }

    const ticketTypes = Array.from(new Set(orderItem.travelerShinkansenTicketTypes()));
    return ticketTypes.join('、');
  }

  airTicketTypeStr() {
    if (this.isDomesticAir()) {
      switch (this.airTicketType) {
        case 0:
          return '変更可';
        case 1:
          return '変更不可/特割';
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  airSeatTypeStr() {
    if (this.isDomesticAir()) {
      return this.airSeatType || '';
    }
    return '';
  }

  ticketingTypeStr() {
    if (this.isDomesticAir()) {
      switch (this.ticketingType) {
        case 0:
          return 'AIトラベル';
        case 1:
          return 'アリーズ';
        case 2:
          return 'エボラブルアジア';
        case 5:
          return 'フロンティア';
        case 6:
          return 'スカイツアーズ';
        case 7:
          return 'ANA（代理店）';
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  rawTicketingExpiredAt(orderItem: OrderItem | undefined) {
    if (this.isForeignAir() && orderItem?.flight) {
      const dateStr = orderItem.flight.homeword?.rawTicketingExpiredAt;
      if (dateStr) {
        const date = dateStr instanceof moment ? dateStr : moment(dateStr);
        return date.format('YYYY年MM月DD日HH時mm分');
      }
    }

    return '';
  }

  carTypeStr() {
    if (this.isRentalCar()) {
      switch (this.carType) {
        case 'compact':
          return 'コンパクト';
        case 'sedan':
          return 'セダン';
        default:
          return this.carTypeOther;
      }
    } else {
      return '';
    }
  }

  mappingDescription() {
    const name = this.carTypeStr() || this.name;
    return `${this.title()}：${name}　${this.from.timeStr()}発`;
  }

  description() {
    return `\
<${this.title()}>
■ご利用：${this.peopleNum}名
■内容
${this.summary()}\
`;
  }

  summary() {
    let s = '';
    if (this.transportType === 'foreign_air') {
      s = `${this.from.foreignAirDescription()}発 → ${this.to.foreignAirDescription()}着 ${this.name}`;
    } else {
      s = `${this.from.description()}発 → ${this.to.description()}着`;
      if (this.name) {
        s += ` ${this.name.replace(/\n/g, ' ')}`;
      }
    }
    if (
      isAiTravel(this.serviceId) &&
      !this.isEvoNumbers() &&
      this.airReservationNumber &&
      this.airConfirmationNumber
    ) {
      s += `\n予約番号:${this.airReservationNumber}`;
      s += `\n確認番号:${this.airConfirmationNumber}`;
    }
    if (this.evoReservationNumber) {
      s += `\n予約番号:${this.evoReservationNumber}`;
    }
    if (this.evoConfirmationNumber) {
      s += `\n確認番号:${this.evoConfirmationNumber}`;
    }
    if (this.evoApplicationNumber) {
      s += `\n問い合わせ番号:${this.evoApplicationNumber}`;
    }
    if (this.evoSkyPdf) {
      s += `\nSKYPDF:${this.evoSkyPdf}`;
    }
    if (this.carProviderOptions && this.carProvider && this.isRentalCar()) {
      const str = this.carProviderOptions
        .filter(option => option[1] === this.carProvider)
        .map(option => `\n提供元:${option[0]}`)
        .join('');
      s += str;
    }
    if (this.carTypeOptions && this.carType) {
      const str = this.carTypeOptions
        .filter(option => option[1] === this.carType)
        .map(option => `\n車種・クラス:${option[0]}`)
        .join('');
      s += str;
    }
    if (this.carTypeOptions && this.carType === 'other' && this.carTypeOther) {
      s += `:${this.carTypeOther}`;
    }
    if (this.lastNameKana && this.firstNameKana) {
      s += `\n運転者氏名（カナ）:${this.lastNameKana} ${this.firstNameKana}`;
    }
    if (this.driverTel) {
      s += `\n運転者電話番号:${toDomesticTel(this.driverTel)}`;
    }
    if (this.remarks) {
      s += `\n備考:${this.remarks}`;
    }
    return s;
  }

  detail(orderItem: OrderItem | undefined = undefined, _opts: unknown = undefined) {
    const lines = this.structuredDetail(orderItem);
    return lines.reduce((acc, line) => {
      return `${acc + (line.label || '') + line.value}\n`;
    }, '');
  }

  structuredDetail(orderItem: OrderItem | undefined = undefined, _opts: unknown = undefined) {
    const lines = [];

    const rawTicketExpiredAt = this.rawTicketingExpiredAt(orderItem);
    if (!_.isEmpty(rawTicketExpiredAt)) {
      lines.push({
        label: '',
        value: `発券期限:${rawTicketExpiredAt}`,
        translate: true
      });
    }

    if (!this.isRailway()) {
      lines.push({
        label: '',
        value: '※表記は現地時刻です。',
        translate: true
      });
    }

    const seatType = this.travelerSeatTypeStr(orderItem);
    if (!_.isEmpty(seatType)) {
      lines.push({
        label: '',
        value: seatType,
        translate: true
      });
    }

    const ticketType = this.travelerTicketTypeStr(orderItem);
    if (!_.isEmpty(ticketType)) {
      lines.push({
        label: '',
        value: ticketType,
        translate: true
      });
    }

    const airTicketType = this.airTicketTypeStr();
    if (!_.isEmpty(airTicketType)) {
      lines.push({
        label: '',
        value: airTicketType,
        translate: true
      });
    }

    const airSeatType = this.airSeatTypeStr();
    if (!_.isEmpty(airSeatType)) {
      lines.push({
        label: '',
        value: airSeatType,
        translate: true
      });
    }

    if (this.isRailway()) {
      lines.push({
        label: '乗車駅:',
        value: this.boardingStation,
        translate: false
      });
      lines.push({
        label: '降車駅:',
        value: this.arrivalStation,
        translate: false
      });
    }

    return lines;
  }

  // tabikobo(smk様向け実験用処理)
  tabikoboAviations() {
    return _.sortBy(this.aviations, item => item.salesTotal / item.numberOfParticipants);
  }

  isDomesticAir() {
    return this.transportType === 'domestic_air' || this.transportType === 'ana_tabisaku';
  }

  isForeignAir() {
    return this.transportType === 'foreign_air';
  }

  isShinkansen() {
    return this.transportType === 'shinkansen';
  }

  isExpress() {
    return this.transportType === 'express';
  }

  isSelectBulkTicket() {
    return this.railwayTicketType !== 2;
  }

  isRailwayTicket() {
    return this.transportType === 'railway_ticket';
  }

  isRentalCar() {
    return this.transportType === 'rental_car';
  }

  isBus() {
    return this.transportType === 'bus';
  }

  isTaxi() {
    return this.transportType === 'taxi';
  }

  isForeignRailway() {
    return this.transportType === 'foreign_railway';
  }

  isAir() {
    return this.isDomesticAir() || this.isForeignAir();
  }

  isRailway() {
    return this.isShinkansen() || this.isExpress() || this.isRailwayTicket() || this.isForeignRailway();
  }

  isHotel() {
    return false;
  }

  isChangeable() {
    return this.airTicketType === 0;
  }

  isAirNumbersMissing() {
    return _.isEmpty(this.airReservationNumber) || _.isEmpty(this.airConfirmationNumber);
  }

  isEvoNumbers() {
    return !_.isEmpty(this.evoReservationNumber || this.evoConfirmationNumber || this.evoApplicationNumber);
  }

  isPackage() {
    return _.includes([2, 3], this.airTicketType);
  }

  ticketType() {
    if (this.isEvoNumbers()) {
      return this.evoTicketType;
    }
    return this.sakuraTicketType;
  }

  seatType() {
    return this.airSeatType;
  }

  marketLogType() {
    if (this.transportType === 'foreign_air') {
      return this.type;
    }
    return this.transportType;
  }

  validationErrors(orderItemStatus: number | undefined = undefined) {
    const errors = [];
    const needStatusGuard = orderItemStatus === 7 || orderItemStatus === 13;

    switch (this.transportType) {
      case 'domestic_air':
        if (this.name === '' || this.name === undefined) {
          errors.push(this.airValidationError());
        }
        if (this.airReservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        if (this.airConfirmationNumber === '' && needStatusGuard) {
          errors.push(this.confirmationNumberValidationError());
        }
        if (orderItemStatus === 7 && (this.airSeatNumber === '' || this.airSeatNumber === undefined)) {
          errors.push(this.anaSeatNumberValidationError());
        }
        break;
      case 'foreign_air':
        if (this.name === '' || this.name === undefined) {
          errors.push(this.airValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'ana_tabisaku':
        if (this.name === '' || this.name === undefined) {
          errors.push(this.airValidationError());
        }
        break;
      case 'shinkansen':
        if (this.boardingStation === '' || this.arrivalStation === '') {
          errors.push(this.shinkansenValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'express':
        if (this.boardingStation === '' || this.arrivalStation === '') {
          errors.push(this.expressValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'railway_ticket':
        if (this.boardingStation === '' || this.arrivalStation === '') {
          errors.push(this.railwayticketValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'foreign_railway':
        if (this.boardingStation === '' || this.arrivalStation === '') {
          errors.push(this.foreignRailwayValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'rental_car':
        if (this.name === '' || this.name === undefined) {
          errors.push(this.rentalcarValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'taxi':
        if (this.name === '' || this.name === undefined) {
          errors.push(this.taxiValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      case 'bus':
        if (this.name === '' || this.name === undefined) {
          errors.push(this.busValidationError());
        }
        if (this.reservationNumber === '' && needStatusGuard) {
          errors.push(this.reservationNumberValidationError());
        }
        break;
      default:
        break;
    }
    if (this.to.time < this.from.time) {
      errors.push(this.timeValidationError());
    }
    return errors;
  }

  updateParams() {
    return {
      id: this.id,
      type: this.type,
      transport_type: this.transportType,
      air_ticket_type: this.airTicketType,
      ticketing_type: this.ticketingType,
      name: this.name,
      people_num: this.peopleNum,
      railway_seat_type: this.railwayTicketType !== 2 ? this.railwaySeatType : null,
      railway_ticket_type: this.railwayTicketType,
      from: this.from.updateParams(),
      to: this.to.updateParams(),
      boarding_station: this.boardingStation,
      arrival_station: this.arrivalStation,
      posting_number: this.postingNumber,
      exic_receipt_number: this.exicReceiptNumber,
      auto_reservation_status: this.autoReservationStatus,
      sold_out: this.soldOut,
      airline_code: this.airlineCode,
      air_reservation_number: this.airReservationNumber,
      air_confirmation_number: this.airConfirmationNumber,
      air_seat_number: this.airSeatNumber,
      evo_ticket_type: this.evoTicketType,
      evo_application_number: this.evoApplicationNumber,
      evo_reservation_number: this.evoReservationNumber,
      evo_confirmation_number: this.evoConfirmationNumber,
      evo_sky_pdf: this.evoSkyPdf,
      sakura_ticket_type: this.sakuraTicketType,
      air_seat_type: this.airSeatType,
      last_name_kana: this.lastNameKana,
      first_name_kana: this.firstNameKana,
      driver_tel: this.driverTel,
      car_type: this.carType,
      car_type_other: this.carTypeOther,
      remarks: this.remarks,
      car_number: this.carNumber,
      car_provider: this.carProvider,
      taxi_service: this.taxiService,
      ticketing_expired_at: this.ticketingExpiredAt,
      allies_application_number: this.alliesApplicationNumber,
      frontier_application_number: this.frontierApplicationNumber,
      skytours_application_number: this.skytoursApplicationNumber,
      ticketing_at: this.ticketingAt ? this.ticketingAt.tz('Asia/Tokyo').format('YYYY-MM-DD') : undefined,
      reservation_number: this.reservationNumber,
      ticket_number: this.ticketNumber,
      cabin: this.cabin,
      transport_element_attach_file: this.transportElementAttachFile
        ? this.transportElementAttachFile.updateParams
        : undefined,
      upload_file_data: this.uploadFileData
    };
  }

  airValidationError() {
    return '便名を入力してください。\n予約が不要な場合、\n航空券の内容を削除してください。';
  }

  reservationNumberValidationError() {
    return '予約番号を入力してください。';
  }

  confirmationNumberValidationError() {
    return '確認番号を入力してください。';
  }

  anaSeatNumberValidationError() {
    return '国内航空券の場合、座席番号を入力してください。';
  }

  shinkansenValidationError() {
    return '出発・到着の場所を入力してください。\n予約が不要な場合、\n新幹線の内容を削除してください。';
  }

  expressValidationError() {
    return '出発・到着の場所を入力してください。\n予約が不要な場合、\n特急の内容を削除してください。';
  }

  railwayticketValidationError() {
    return '出発・到着の場所を入力してください。\n予約が不要な場合、\n在来線の内容を削除してください。';
  }

  foreignRailwayValidationError() {
    return '出発・到着の場所を入力してください。\n予約が不要な場合、\n海外鉄道の内容を削除してください。';
  }

  rentalcarValidationError() {
    return '便名を入力してください。\n予約が不要な場合、\nレンタカーの内容を削除してください。';
  }

  taxiValidationError() {
    return '便名を入力してください。\n予約が不要な場合、\nタクシーの内容を削除してください。';
  }

  busValidationError() {
    return '便名を入力してください。\n予約が不要な場合、\nバスの内容を削除してください。';
  }

  timeValidationError() {
    return '出発以降を指定してください。';
  }
}

export default TransportElement;
